import React, { useContext, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Dropdown, DropdownItem, DropdownMenu } from 'semantic-ui-react'
import ClimaCheckQuery from '~/QueryHooks/ClimaChQuery'
import Layout from '~/components/Layout'
import TableComponent from '~/components/TableComponent'
import i18n from '~/components/i18n'
import { DownloadService } from '~/api/services/downloadFiles'
import { handleErrors } from '~/utils/helpers'
import ClimaCheckService from '~/api/services/climaCheck'
import { ThemeContext } from 'styled-components'
import dayjs from 'dayjs'

const getTableColumnHeaders = () => [
  { name: i18n.t('Reference period'), key: 'period', type: 'periods', sorted: 'desc' },
  { name: i18n.t('Number of Data Collections'), key: 'rows', sorted: 'desc' },
  { name: i18n.t('Export erstellt am'), key: 'createdAt', type: 'date', format: 'DD.MM.YYYY HH:mm', sorted: 'desc' },
  { name: i18n.t('Name of the creator'), key: 'creator', type: 'creatorName' },
  { name: i18n.t('Actions') }
]

const ClimaExports: React.FC = () => {
  const { t } = useTranslation()
  const intervalGeneratingExports = useRef(null)
  const { firstColor } = useContext(ThemeContext)
  const actions = [
    {
      popupMessage: 'Klimabilanzierungen herunterladen',
      type: 'icon',
      name: 'cloud download',
      size: 'large',
      actionType: 'download'
    }
  ]

  const [dataFiltered, setDataFiltered] = useState({
    page: 0,
    rows: 20,
    sort: ''
  })
  const [tableColumnHeaders, setTableColumnHeaders] = useState(getTableColumnHeaders())
  const [loadingAction, setLoadingAction] = useState({ type: '', id: null, value: false, idKey: '' })

  const {
    data: exportOptions,
    refetch: fetchOptions,
    isFetching: isFetchingOptions,
    isLoading: isBusyOptions
  } = ClimaCheckQuery.getClimaExportOptionsData()

  const {
    data: exportsData,
    isLoading: isBusyExports,
    refetch: fetchExports,
    isFetching: isFetchingExports
  } = ClimaCheckQuery.getClimaExports({
    ...dataFiltered,
    page: dataFiltered.page === 0 ? dataFiltered.page : dataFiltered.page - 1
  })

  async function generateExport(value) {
    try {
      const periods = value ? value.toString().split('/') : ['', '']
      await ClimaCheckService.generateClimaExport({ periodFrom: periods[0], periodTo: periods[1] })
      fetchOptions()
      intervalGeneratingExports.current = setInterval(() => {
        fetchOptions()
      }, 10000)
    } catch (error) {
      handleErrors(error)
    }
  }

  useEffect(() => {
    setTableColumnHeaders(getTableColumnHeaders())
  }, [t])

  useEffect(() => {
    return () => {
      clearInterval(intervalGeneratingExports.current)
    }
  }, [])

  useEffect(() => {
    fetchExports()
  }, [dataFiltered])

  useEffect(() => {
    if (exportOptions?.activeJob === 1 && !intervalGeneratingExports.current) {
      intervalGeneratingExports.current = setInterval(() => {
        fetchOptions()
      }, 10000)
    } else if (exportOptions?.activeJob === 0 && intervalGeneratingExports.current && !isFetchingOptions) {
      clearInterval(intervalGeneratingExports.current)
      intervalGeneratingExports.current = null
      fetchExports()
    }
  }, [exportOptions, isFetchingOptions])

  const isGeneratingActive = exportOptions?.activeJob === 1 || isFetchingOptions

  async function handleActionClick(row, type) {
    if (type !== 'download') return

    setLoadingAction({ type, id: row.id, value: true, idKey: 'id' })
    try {
      await DownloadService.downloadFileWithName(`climate-check/exports/${row.id}/download`)
    } catch (error) {
      handleErrors(error)
    }
    setLoadingAction({ type: '', id: null, value: false, idKey: '' })
  }

  function renderActions() {
    return <>
      <Dropdown
        text={t('Export download')}
        icon='download'
        floating
        labeled
        button
        loading={isGeneratingActive}
        disabled={isGeneratingActive}
        className='icon'
        style={{ background: firstColor, color: 'white' }}
      >
        <DropdownMenu style={{ maxHeight: 400, overflowY: 'auto' }}>
          {exportOptions?.periods?.map(period => {
            return <DropdownItem
              key={Math.random()}
              onClick={() => generateExport(`${period.periodFrom}/${period.periodTo}`)}
            >
              {`BZ ${dayjs(period.periodFrom).format('DD.MM.YYYY')} - ${dayjs(period.periodTo).format('DD.MM.YYYY')}`}
            </DropdownItem>
          })}
        </DropdownMenu>
      </Dropdown>
    </>
  }

  const { filtered, rows } = exportsData ? exportsData : { filtered: 0, rows: [] }
  const isLoading = isBusyOptions || isBusyExports

  return <Layout
    title={'Übersicht Export Klimabilanzierungen'}
    childContainerSize={'300px'}
    fontSizeHeader={'18px'}
    actions={renderActions()}
    useSpinner
    isBusy={isLoading}
  >
    <TableComponent
      isFetchingData={isFetchingExports}
      animated
      loading={isLoading}
      loadingAction={loadingAction}
      actions={actions}
      tableColumnHeaders={tableColumnHeaders}
      dataFiltered={dataFiltered}
      handleClickAction={(row, type) => handleActionClick(row, type)}
      handleChangeDataFiltered={(data, headers) => {
        setDataFiltered(data)
        setTableColumnHeaders(headers)
      }}
      rows={rows}
      total={filtered}
    />
  </Layout>
}
export default ClimaExports
